import {
  ComponentType,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useLocation } from 'react-router-dom';

import { useApi } from '@backstage/core-plugin-api';

import { PageId, trackingApiRef } from '../../apis';
import { PageTrackingContext } from '../../contexts/PageTrackingContext';
import { PageViewContext } from '../../contexts/PageViewContext';
import { ElementId, TrackingData } from '../../types';
import { TrackingScope } from '../TrackingScope';

export const PageImpression = ({
  pageId,
  data,
  children,
  scopeRole = 'main',
}: {
  pageId: PageId;
  data?: TrackingData;
  children: ReactNode;
  scopeRole?: string;
}) => {
  const tracking = useApi(trackingApiRef);
  const location = useLocation();
  const { currentPageView, setPageView } = useContext(PageTrackingContext);
  const parentPageView = useContext(PageViewContext);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    if (parentPageView.pageId === pageId) {
      // eslint-disable-next-line no-console
      console.warn(
        `Duplicate page impression for pageId "${pageId}": this <PageImpression> is nested inside another one with the same pageId, so every view of this page is tracked twice. ` +
          'Pages that render Page.Root (directly or via a shared component that takes a routeRef) fire their impression automatically — remove the extra withPageImpression() / <PageImpression> wrapper.',
      );
    }
  }, [pageId, parentPageView.pageId]);

  const pageViewId = useMemo(
    () => crypto.randomUUID(),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: regenerate UUID when pageId or route changes
    [pageId, location.pathname],
  );

  // Capture "from" during render (before any effects run)
  const capturedFrom = useRef<{
    pageId: PageId | null;
    pageViewId: string | null;
  }>({ pageId: null, pageViewId: null });
  const capturedForPageViewId = useRef<string | null>(null);
  if (capturedForPageViewId.current !== pageViewId) {
    capturedFrom.current = { ...currentPageView };
    capturedForPageViewId.current = pageViewId;
  }

  useEffect(() => {
    setPageView(pageId, pageViewId);
    tracking.trackPageImpression({
      pageId,
      pageViewId,
      fromPageId: capturedFrom.current.pageId,
      fromPageViewId: capturedFrom.current.pageViewId,
      data: data ?? {},
    });
  }, [pageViewId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PageViewContext.Provider value={{ pageId, pageViewId }}>
      <TrackingScope
        elementId={`${scopeRole}.${pageId}` as ElementId}
        data={data}
      >
        {children}
      </TrackingScope>
    </PageViewContext.Provider>
  );
};

export function withPageImpression<P extends object>(
  Component: ComponentType<P>,
  options: { pageId: PageId; scopeRole?: string },
) {
  const Wrapped = (props: P) => (
    <PageImpression pageId={options.pageId} scopeRole={options.scopeRole}>
      <Component {...props} />
    </PageImpression>
  );
  Wrapped.displayName = `withPageImpression(${Component.displayName ?? Component.name ?? 'Component'})`;
  return Wrapped;
}
